import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Trash, ArrowClockwise, SignOut, Package } from "@phosphor-icons/react";
import api, { fmtErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const statusColor = {
  "Booked": "bg-soft-yellow text-brand-green", "Picked Up": "bg-blue-50 text-blue-700",
  "Washing": "bg-cyan-50 text-cyan-700", "Ready": "bg-soft-green text-brand-green",
  "Out for Delivery": "bg-orange-50 text-orange-700", "Delivered": "bg-brand-green text-white",
};

export default function Account() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState(null);
  const [addr, setAddr] = useState({ label: "Home", street: "", suburb: "", postcode: "", state: "NSW" });

  useEffect(() => { document.title = "My Account | Zaekids Laundromat"; }, []);
  useEffect(() => {
    if (user === false) navigate("/login");
    if (user) api.get("/my/bookings").then((r) => setBookings(r.data));
  }, [user, navigate]);

  if (!user) return <div className="py-32 text-center font-bold text-brand-green">Loading…</div>;

  const addAddress = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/my/addresses", addr);
      setUser(data);
      setAddr({ label: "Home", street: "", suburb: "", postcode: "", state: "NSW" });
      toast.success("Address saved!");
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const removeAddress = async (i) => {
    const { data } = await api.delete(`/my/addresses/${i}`);
    setUser(data);
  };

  const input = "w-full rounded-2xl border-2 border-brand-green/15 bg-white px-4 py-2.5 text-sm font-semibold outline-none focus:border-brand-green";

  return (
    <div data-testid="account-page" className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <span className="font-script text-2xl font-bold text-brand-yellow-dark">G'day, {user.name?.split(" ")[0]}!</span>
          <h1 className="text-3xl font-black tracking-tight text-brand-green">My Account</h1>
        </div>
        <button data-testid="logout-btn" onClick={() => { logout(); navigate("/"); }}
          className="flex items-center gap-2 rounded-full border-2 border-red-200 px-5 py-2.5 text-sm font-extrabold text-red-600 hover:bg-red-50">
          <SignOut size={18} weight="bold" /> Log Out
        </button>
      </div>

      <div className="mt-10 grid gap-8 lg:grid-cols-[1fr_340px]">
        <div>
          <h2 className="flex items-center gap-2 text-xl font-extrabold text-brand-green"><Package size={24} weight="duotone" /> Booking History</h2>
          <div className="mt-4 space-y-4">
            {bookings === null ? <p className="text-slate-500">Loading bookings…</p> :
              bookings.length === 0 ? (
                <div className="rounded-3xl bg-white p-8 text-center shadow-[0_8px_30px_rgb(27,94,32,0.08)]">
                  <p className="font-bold text-slate-500">No bookings yet — your fresh-laundry journey starts here!</p>
                  <button data-testid="account-book-btn" onClick={() => navigate("/book")}
                    className="btn-bounce mt-4 rounded-full bg-brand-green px-6 py-3 font-extrabold text-white">Book a Pick Up</button>
                </div>
              ) : bookings.map((b) => (
                <div key={b.id} data-testid={`booking-card-${b.order_number}`} className="rounded-3xl bg-white p-6 shadow-[0_8px_30px_rgb(27,94,32,0.08)]">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-lg font-black text-brand-green">{b.order_number}</p>
                    <span className={`rounded-full px-3 py-1 text-xs font-extrabold ${statusColor[b.status]}`}>{b.status}</span>
                  </div>
                  <p className="mt-2 text-sm text-slate-600">
                    {b.services.map((s) => s[0].toUpperCase() + s.slice(1)).join(", ")} · {b.pickup_date} · {b.time_slot}
                  </p>
                  <p className="text-sm font-bold text-brand-green">${b.pricing.total.toFixed(2)}</p>
                  <div className="mt-3 flex gap-3">
                    <button data-testid={`rebook-btn-${b.order_number}`} onClick={() => navigate("/book", { state: { rebook: b } })}
                      className="flex items-center gap-1 rounded-full bg-soft-green px-4 py-2 text-xs font-extrabold text-brand-green hover:bg-brand-green hover:text-white">
                      <ArrowClockwise size={14} weight="bold" /> Book Again
                    </button>
                    <button onClick={() => navigate("/track", { state: { order_number: b.order_number, phone: b.phone } })}
                      className="rounded-full bg-soft-yellow px-4 py-2 text-xs font-extrabold text-brand-green">Track</button>
                  </div>
                </div>
              ))}
          </div>
        </div>

        <div>
          <h2 className="flex items-center gap-2 text-xl font-extrabold text-brand-green"><MapPin size={24} weight="duotone" /> Saved Addresses</h2>
          <div className="mt-4 space-y-3">
            {(user.addresses || []).map((a, i) => (
              <div key={i} data-testid={`address-item-${i}`} className="flex items-start justify-between gap-2 rounded-2xl bg-white p-4 shadow-sm">
                <p className="text-sm"><strong className="text-brand-green">{a.label}:</strong> {a.street}, {a.suburb} {a.state} {a.postcode}</p>
                <button data-testid={`delete-address-${i}`} onClick={() => removeAddress(i)} className="text-red-400 hover:text-red-600" aria-label="Delete address">
                  <Trash size={18} weight="duotone" />
                </button>
              </div>
            ))}
          </div>
          <form onSubmit={addAddress} className="mt-4 space-y-3 rounded-3xl bg-white p-5 shadow-[0_8px_30px_rgb(27,94,32,0.08)]">
            <p className="text-sm font-extrabold text-brand-green">Add a new address</p>
            <input data-testid="new-address-label" className={input} placeholder="Label (Home, Work…)" value={addr.label} onChange={(e) => setAddr({ ...addr, label: e.target.value })} />
            <input data-testid="new-address-street" className={input} required placeholder="Street address" value={addr.street} onChange={(e) => setAddr({ ...addr, street: e.target.value })} />
            <div className="flex gap-2">
              <input data-testid="new-address-suburb" className={input} required placeholder="Suburb" value={addr.suburb} onChange={(e) => setAddr({ ...addr, suburb: e.target.value })} />
              <input data-testid="new-address-postcode" className={`${input} w-28`} required maxLength={4} placeholder="2000" value={addr.postcode} onChange={(e) => setAddr({ ...addr, postcode: e.target.value.replace(/\D/g, "") })} />
            </div>
            <button type="submit" data-testid="save-new-address-btn" className="btn-bounce w-full rounded-full bg-brand-green py-3 text-sm font-extrabold text-white">Save Address</button>
          </form>
        </div>
      </div>
    </div>
  );
}
